import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Form, InputGroup } from 'react-bootstrap';
import { isNfcSupported } from '../../../utils/useNfcReader';

const NFC_BASE_URL = 'https://local.tomi8.co';

export const buildNfcUrl = (serial) => `${NFC_BASE_URL}?${encodeURIComponent(serial)}`;

const isCameraSupported = () =>
  typeof window !== 'undefined' &&
  'BarcodeDetector' in window &&
  !!navigator.mediaDevices?.getUserMedia;

const isLockSupported = () =>
  typeof window !== 'undefined' &&
  'NDEFReader' in window &&
  typeof window.NDEFReader.prototype.makeReadOnly === 'function';

const nfcErrorMessage = (e) => {
  switch (e?.name) {
    case 'NotAllowedError':
      return 'Permiso NFC denegado.';
    case 'NotSupportedError':
      return 'El NFC no está disponible. Verifica que esté encendido.';
    case 'NetworkError':
      return 'No se pudo escribir en el tag. Mantenlo pegado al celular e inténtalo de nuevo.';
    default:
      return e?.message || 'Error NFC desconocido.';
  }
};

/**
 * Escribe en un tag NFC la URL https://local.tomi8.co?SERIAL y la verifica.
 * El serial se ingresa a mano o con la cámara (código de barras).
 */
const NfcWriter = () => {
  const [serial, setSerial] = useState('');
  // idle | preparing | writing | written | verifying | verified | mismatch | locking | locked | error
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const [cameraOn, setCameraOn] = useState(false);
  const [cameraError, setCameraError] = useState('');

  const nfcAbortRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const scanTimerRef = useRef(null);
  const guardRef = useRef(null);

  const cleanSerial = serial.trim();
  const nfcSupported = isNfcSupported();
  const cameraSupported = isCameraSupported();
  const lockSupported = isLockSupported();
  const busy = ['preparing', 'writing', 'verifying', 'locking'].includes(status);

  const cancelNfc = useCallback(() => {
    if (nfcAbortRef.current) {
      nfcAbortRef.current.abort();
      nfcAbortRef.current = null;
    }
  }, []);

  // Chrome solo intercepta el tag (y Android no abre su URL) mientras haya un scan() activo.
  // Lo dejamos corriendo en esta página; además pide el permiso NFC la primera vez.
  const ensureGuard = async () => {
    if (guardRef.current) return;
    const controller = new AbortController();
    const reader = new window.NDEFReader();
    reader.onreading = () => {};
    await reader.scan({ signal: controller.signal });
    guardRef.current = controller;
  };

  const stopCamera = useCallback(() => {
    clearInterval(scanTimerRef.current);
    scanTimerRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCameraOn(false);
  }, []);

  useEffect(() => () => {
    guardRef.current?.abort();
    guardRef.current = null;
    cancelNfc();
    stopCamera();
  }, [cancelNfc, stopCamera]);

  const startCamera = async () => {
    setCameraError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
      });
      streamRef.current = stream;
      setCameraOn(true);
      // El <video> se monta al activar cameraOn; esperamos al siguiente ciclo.
      setTimeout(() => {
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = stream;
        video.play().catch(() => {});
        const detector = new window.BarcodeDetector();
        scanTimerRef.current = setInterval(async () => {
          if (video.readyState < 2) return;
          try {
            const codes = await detector.detect(video);
            if (codes.length > 0 && codes[0].rawValue) {
              setSerial(codes[0].rawValue.trim());
              setStatus('idle');
              setMessage('');
              stopCamera();
            }
          } catch (e) {
            // frame no legible: seguimos intentando
          }
        }, 300);
      }, 0);
    } catch (e) {
      setCameraError(
        e?.name === 'NotAllowedError'
          ? 'Permiso de cámara denegado.'
          : 'No se pudo abrir la cámara.'
      );
    }
  };

  const handleWrite = async () => {
    if (!cleanSerial || busy) return;
    stopCamera();
    const controller = new AbortController();
    nfcAbortRef.current = controller;
    setStatus('preparing');
    setMessage('');
    try {
      await ensureGuard();
      if (controller.signal.aborted) return;
      setStatus('writing');
      const writer = new window.NDEFReader();
      await writer.write(
        { records: [{ recordType: 'url', data: buildNfcUrl(cleanSerial) }] },
        { overwrite: true, signal: controller.signal }
      );
      setStatus('written');
      setMessage(`Tag grabado con ${buildNfcUrl(cleanSerial)}. Ahora puedes verificarlo.`);
    } catch (e) {
      if (e?.name === 'AbortError') {
        setStatus('idle');
        setMessage('');
      } else {
        setStatus('error');
        setMessage(nfcErrorMessage(e));
      }
    } finally {
      if (nfcAbortRef.current === controller) nfcAbortRef.current = null;
    }
  };

  const handleVerify = async () => {
    if (!cleanSerial || busy) return;
    stopCamera();
    const controller = new AbortController();
    nfcAbortRef.current = controller;
    const expected = buildNfcUrl(cleanSerial);
    setStatus('verifying');
    setMessage('');
    try {
      const reader = new window.NDEFReader();
      reader.onreadingerror = () => {
        setStatus('error');
        setMessage('No se pudo leer el tag. Acércalo de nuevo.');
        controller.abort();
      };
      reader.onreading = (event) => {
        const urls = event.message.records
          .filter((r) => r.recordType === 'url' || r.recordType === 'text')
          .map((r) => {
            try {
              return new TextDecoder(r.encoding || 'utf-8').decode(r.data);
            } catch (err) {
              return '';
            }
          });
        controller.abort();
        if (urls.includes(expected)) {
          setStatus('verified');
          setMessage(`Verificado: el tag contiene ${expected}`);
        } else {
          setStatus('mismatch');
          setMessage(
            urls.length > 0
              ? `El tag NO coincide. Contiene: ${urls.join(' , ')}`
              : 'El tag no contiene una URL.'
          );
        }
      };
      await reader.scan({ signal: controller.signal });
    } catch (e) {
      if (e?.name !== 'AbortError') {
        setStatus('error');
        setMessage(nfcErrorMessage(e));
      }
    } finally {
      if (nfcAbortRef.current === controller) nfcAbortRef.current = null;
    }
  };

  const handleLock = async () => {
    if (!cleanSerial || busy) return;
    if (!window.confirm(
      `Vas a BLOQUEAR el tag de ${cleanSerial} en modo solo lectura. Es IRREVERSIBLE: no se podrá volver a escribir. ¿Continuar?`
    )) return;
    const controller = new AbortController();
    nfcAbortRef.current = controller;
    setStatus('locking');
    setMessage('');
    try {
      await ensureGuard();
      if (controller.signal.aborted) return;
      await new window.NDEFReader().makeReadOnly({ signal: controller.signal });
      setStatus('locked');
      setMessage('Tag bloqueado: ya no se puede reescribir.');
    } catch (e) {
      if (e?.name === 'AbortError') {
        setStatus('idle');
        setMessage('');
      } else {
        setStatus('error');
        setMessage(nfcErrorMessage(e));
      }
    } finally {
      if (nfcAbortRef.current === controller) nfcAbortRef.current = null;
    }
  };

  const handleCancel = () => {
    cancelNfc();
    setStatus('idle');
    setMessage('');
  };

  const statusAlert = () => {
    if (status === 'preparing') {
      return <Alert variant="warning">Concede el permiso NFC si Chrome lo pide. Todavía NO acerques el tag.</Alert>;
    }
    if (status === 'locking') {
      return <Alert variant="warning">Acerca el tag para bloquearlo (irreversible)…</Alert>;
    }
    if (status === 'writing') {
      return <Alert variant="info">Acerca el tag NFC a la parte trasera del celular y mantenlo quieto…</Alert>;
    }
    if (status === 'verifying') {
      return <Alert variant="info">Acerca el tag NFC para leerlo y compararlo…</Alert>;
    }
    if (status === 'written' || status === 'verified' || status === 'locked') {
      return <Alert variant="success">{message}</Alert>;
    }
    if (status === 'mismatch' || status === 'error') {
      return <Alert variant="danger">{message}</Alert>;
    }
    return null;
  };

  return (
    <div className="container py-4" style={{ maxWidth: 560 }}>
      <h3 className="mb-3">NFC Writer</h3>

      {!nfcSupported && (
        <Alert variant="warning">
          Este navegador no soporta escritura NFC. Usa Chrome en un Android con NFC encendido.
        </Alert>
      )}

      <Card className="mb-3">
        <Card.Body>
          <Form.Label htmlFor="nfc-serial">Serial</Form.Label>
          <InputGroup className="mb-2">
            <Form.Control
              id="nfc-serial"
              value={serial}
              placeholder="Ej: T128000751"
              disabled={busy}
              autoComplete="off"
              onChange={(e) => {
                setSerial(e.target.value);
                setStatus('idle');
                setMessage('');
              }}
            />
            {cameraSupported && (
              <Button
                variant={cameraOn ? 'danger' : 'outline-secondary'}
                disabled={busy}
                onClick={cameraOn ? stopCamera : startCamera}
                title="Leer código de barras con la cámara"
              >
                <i className={`bi ${cameraOn ? 'bi-x-lg' : 'bi-camera'}`}></i>
              </Button>
            )}
          </InputGroup>

          {!cameraSupported && (
            <Form.Text className="text-muted d-block mb-2">
              La lectura con cámara requiere Chrome en Android.
            </Form.Text>
          )}
          {cameraError && <div className="text-danger small mb-2">{cameraError}</div>}

          {cameraOn && (
            <video
              ref={videoRef}
              playsInline
              muted
              className="w-100 rounded mb-2 bg-dark"
              style={{ maxHeight: 280, objectFit: 'cover' }}
            />
          )}

          {cleanSerial && (
            <div className="small text-muted mb-3">
              Se grabará: <code>{buildNfcUrl(cleanSerial)}</code>
            </div>
          )}

          <div className="d-flex gap-2 flex-wrap">
            <Button
              variant="primary"
              disabled={!nfcSupported || !cleanSerial || busy}
              onClick={handleWrite}
            >
              Escribir
            </Button>
            <Button
              variant="outline-primary"
              disabled={!nfcSupported || !cleanSerial || busy}
              onClick={handleVerify}
            >
              Verificar
            </Button>
            {lockSupported && (
              <Button
                variant="outline-danger"
                disabled={!nfcSupported || !cleanSerial || busy || status !== 'verified'}
                onClick={handleLock}
                title="Disponible después de verificar. Irreversible."
              >
                Bloquear
              </Button>
            )}
            {busy && (
              <Button variant="outline-secondary" onClick={handleCancel}>
                Cancelar
              </Button>
            )}
          </div>
        </Card.Body>
      </Card>

      {!lockSupported && nfcSupported && (
        <Alert variant="secondary" className="small">
          Esta versión de Chrome no permite bloquear el tag desde la web, así que queda regrabable.
        </Alert>
      )}

      {statusAlert()}
    </div>
  );
};

export default NfcWriter;
